import React from "react";
import type { DashboardWidget } from "../../../types/dashboard/dashboard.types";

// ✅ Tes widgets existants (selon tes fichiers)
import Notification from "./widgets/alerts";
import FleetCo2 from "./widgets/fleetCo2";
import FleetCounter from "./widgets/fleetCounter";
import FleetSate from "./widgets/fleetSate";
import StatsComponent from "./widgets/StatsComponent";
import TransmissionGapChart from "./widgets/TransmissionGapChart";

type WidgetConfig = Record<string, any>;

function safeParseConfig(content: string | undefined): WidgetConfig {
    if (!content) return {};
    try {
        const parsed = JSON.parse(content);
        return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
        return {};
    }
}

function normalizeTitleLevel(value: unknown): "h1" | "h2" | "h3" | "h4" {
    return value === "h1" || value === "h2" || value === "h4" ? value : "h3";
}

export default function WidgetRenderer({ widget }: { widget: DashboardWidget }) {
    const config = safeParseConfig(widget.content);

    switch (widget.type) {
        /**
         * ✅ ALERTS / NOTIFICATIONS
         * Ton composant attend: { Notifications } :contentReference[oaicite:5]{index=5}
         * On prendra config.Notifications (array) sinon []
         */
        case "alerts":
            return (
                <Notification
                    id={widget.id}
                    type="notifications"
                    Notifications={config.Notifications ?? []}
                />
            );



        /**
         * ✅ FLEET STATE PIE
         * Ton composant attend: { options } :contentReference[oaicite:6]{index=6}
         */
        case "fleet_state":
            return (
                <FleetSate
                    title={String(config.title ?? "Apercu de votre flotte")}
                    refreshSeconds={Number(config.refreshSeconds ?? 60)}
                    options={{
                        DrivingValue: Number(config.DrivingValue ?? 0),
                        ParkingValue: Number(config.ParkingValue ?? 0),
                        ParkingEngineRunningValue: Number(config.ParkingEngineRunningValue ?? 0),
                        LastTransmissionValue: Number(config.LastTransmissionValue ?? 0),
                    }}
                />
            );

        /**
         * ✅ FLEET CO2
         * Ton composant déclare props "data" mais ne l'utilise pas :contentReference[oaicite:7]{index=7}
         * On peut quand même passer config.data si tu l’ajoutes plus tard.
         */
        case "fleet_co2":
            return <FleetCo2 data={config.data} periodHours={Number(config.periodHours ?? 24)} title={String(config.title ?? "CO2")} /> as any;

        /**
         * ✅ FLEET COUNTER
         * Ton composant attend:
         * numberOfItem, title, icon, color, linkTo :contentReference[oaicite:8]{index=8}
         */
        case "fleet_counter":
            return (
                <FleetCounter
                    numberOfItem={Number(config.numberOfItem ?? 0)}
                    title={String(config.title ?? "Counter")}
                    icon={String(config.icon ?? "car")}
                    color={String(config.color ?? "bg-primary")}
                    linkTo={String(config.linkTo ?? "/vehicles")}
                    metricKey={String(config.metricKey ?? "custom")}
                />
            );

        /**
         * ✅ STATS (Speedometer)
         * Ton composant attend psn :contentReference[oaicite:9]{index=9}
         */
        case "stats":
            return (
                <StatsComponent
                    psn={String(config.psn ?? "")}
                    type={"statsComponent"}
                    id={""}
                    periodHours={Number(config.periodHours ?? 24)}
                    title={String(config.title ?? "Vitesse")}
                />
            );

        case "transmission_gap":
            return (
                <TransmissionGapChart
                    title={String(config.title ?? "Absence transmission")}
                    thresholdHours={Number(config.thresholdHours ?? 1)}
                    refreshSeconds={Number(config.refreshSeconds ?? 60)}
                />
            );

        case "content_html": {
            const TitleTag = normalizeTitleLevel(config.titleLevel);
            const align = ["left", "center", "right"].includes(String(config.align)) ? String(config.align) : "left";
            const title = String(config.title ?? "");
            const text = String(config.text ?? "");
            const html = String(config.html ?? "");

            return (
                <div className={`dash2-content-widget align-${align}`}>
                    {title && <TitleTag className="dash2-content-widget-title">{title}</TitleTag>}
                    {text && <p className="dash2-content-widget-text">{text}</p>}
                    {html && <div className="dash2-content-widget-html" dangerouslySetInnerHTML={{ __html: html }} />}
                </div>
            );
        }

        default:
            return (
                <div style={{ opacity: 0.7 }}>
                    Type widget inconnu: <b>{widget.type}</b>
                    <br />
                    content: {widget.content}
                </div>
            );
    }
}
